import {useEffect, useRef} from "react";
import {useLocation} from "react-router-dom";

import {SidebarInset} from "@/components/ui/sidebar";
import {cn} from "@/lib/utils";

import {useIsMobile} from "../hooks";
import {useSyncContentColumnRight} from "../hooks/use-sync-content-column-right";
import useSafeAreaInsets from "../hooks/use-safe-area-insets";
import {useStoredMe} from "../store/me";
import {useUserPropertyStore} from "../store/user-property";
import {TooltipProvider} from "@/components/ui/tooltip";

import {NotificationEnablePushBanner} from "@/src/modules/notification-feed/components/notification-enable-push-banner";

import ConnectionBanner from "./connection-banner";
import ImpersonationBanner from "@/src/modules/support/components/impersonation-banner";
import MobileHeader from "./mobile-header";
import Navbar from "./navbar";

export default function Layout({children}: {children: React.ReactNode}) {
  const isMobile = useIsMobile();
  const me = useStoredMe();
  const isResidentShell = Boolean(me?.isResident);
  const currentProperty = useUserPropertyStore((s) => s.userProperty);
  const location = useLocation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useSafeAreaInsets();
  useSyncContentColumnRight(scrollContainerRef);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    scrollContainer.scrollTo({top: 0, left: 0, behavior: "auto"});
  }, [location.pathname, location.search]);

  return (
    <div className="relative flex h-full min-h-0 w-full">
      {!isResidentShell ? <Navbar /> : null}

      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
        <SidebarInset>
          <section className="flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-gray-100">
            <ConnectionBanner />
            <ImpersonationBanner />
            <NotificationEnablePushBanner variant="layout" dismissible />
            {isMobile ? <MobileHeader /> : null}
            <TooltipProvider>
              <div
                key={currentProperty?.id ?? "no-property"}
                data-scroll-container
                ref={scrollContainerRef}
                className={cn(
                  "min-h-0 flex-1 touch-pan-y overflow-y-auto overflow-x-hidden overscroll-y-contain",
                  "[-webkit-overflow-scrolling:touch]",
                  isMobile &&
                    "pb-[max(0.75rem,var(--app-safe-area-bottom,env(safe-area-inset-bottom,0px)))]",
                )}
              >
                {children}
              </div>
            </TooltipProvider>
          </section>
        </SidebarInset>
      </div>
    </div>
  );
}
